import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Link from '@/components/link';
import type { UseGetTablePropsOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTablePropsWithoutUrlHash } from '@/utils/table/hooks/useGetTablePropsWithoutUrlHash';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { oversightAssessmentDetailsUrl } from '@/utils/urls';

import { UNRATED } from '../../../../controls/lookupData';
import type {
  ComplianceDocumentAssessmentResultFlatFields,
  ComplianceDocumentAssessmentResultRegisterFields,
} from './types';
import { useOversightDocumentRatingLabelledFields } from './useOversightDocumentRatingLabelledFields';

const useGetFieldConfig = (
  onOpenResult: (id: string) => void
): TableFields<ComplianceDocumentAssessmentResultRegisterFields> => {
  const { t: at } = useTranslation(['common'], {
    keyPrefix: 'complianceMonitoringAssessment',
  });

  const { getByValue: getResultByValue } = useRating(
    'performance_result',
    'oversight'
  );
  const { getByValue: statusGetByValue } = useRating(
    'assessment_status',
    'oversight'
  );

  return useMemo<TableFields<ComplianceDocumentAssessmentResultRegisterFields>>(
    () => ({
      TestDate: dateColumnFromConfig({
        header: { formId: 'document_oversight_result', fieldId: 'TestDate' },
        dateField: 'TestDate',
        onClick: (item) => onOpenResult(item.Id),
      }),
      Title: {
        formId: 'oversight_assessment',
        fieldId: 'Title',
        includeFromTypePostfix: true,
        cell: (item) =>
          item.parents.find((p) => p.oversightAssessment)
            ?.oversightAssessment ? (
            <Link
              variant={'secondary'}
              href={oversightAssessmentDetailsUrl(
                item.parents.find((p) => p.oversightAssessment)!
                  .oversightAssessment!.Id
              )}
            >
              {
                item.parents.find((p) => p.oversightAssessment)
                  ?.oversightAssessment?.Title
              }
            </Link>
          ) : (
            '-'
          ),
        sortingField: 'Title',
        isRowHeader: true,
      },
      Result: {
        formId: 'document_oversight_result',
        fieldId: 'Rating',
        cell: (item) => {
          const result = getResultByValue(item.ResultValue);

          return <SimpleRatingBadge rating={result ?? UNRATED} />;
        },
        sortingField: 'ResultValue',
      },
      Status: {
        header: at('columns.Status'),
        cell: (item) => {
          return <SimpleRatingBadge rating={statusGetByValue(item.Status)} />;
        },
        sortingField: 'Status',
      },
      CompletionDate: dateColumnFromConfig({
        header: {
          formId: 'oversight_assessment',
          fieldId: 'ActualCompletionDate',
          includeFromTypePostfix: true,
        },
        dateField: 'ActualCompletionDate',
      }),
      NextTestDate: dateColumnFromConfig({
        header: {
          formId: 'oversight_assessment',
          fieldId: 'NextTestDate',
          includeFromTypePostfix: true,
        },
        dateField: 'NextTestDate',
      }),
    }),
    [at, getResultByValue, onOpenResult, statusGetByValue]
  );
};

const useGetDocumentRatingTableProps = (
  records: ComplianceDocumentAssessmentResultFlatFields[] | undefined,
  onOpenResult: (id: string) => void
): UseGetTablePropsOptions<ComplianceDocumentAssessmentResultRegisterFields> => {
  const labelledFields = useOversightDocumentRatingLabelledFields(records);
  const fields = useGetFieldConfig(onOpenResult);

  return {
    customAttributeFormIds: ['document_oversight_result'],
    data: labelledFields,
    enableFiltering: true,
    entityLabel: 'rating',
    defaultSortingState: {
      sortingColumn: 'TestDate',
      sortingDirection: 'desc',
    },
    initialColumns: [
      'TestDate',
      'Title',
      'Result',
      'Status',
      'CompletionDate',
      'NextTestDate',
    ],
    preferencesStorageKey: 'OversightDocumentRatingRegisterTable-Preferences',
    fields,
  };
};

export const useGetCollectionTableProps = (
  records: ComplianceDocumentAssessmentResultFlatFields[] | undefined,
  onOpenResult: (id: string) => void
): TablePropsWithActions<ComplianceDocumentAssessmentResultRegisterFields> => {
  const props = useGetDocumentRatingTableProps(records, onOpenResult);

  return useGetTablePropsWithoutUrlHash(props);
};
